import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { CTABlock } from "@/components/calv/CTABlock";
import { Callout } from "@/components/calv/Callout";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { EinzugsgebietMap } from "@/components/calv/EinzugsgebietMap";

export const Route = createFileRoute("/ueber-uns")({
  head: () => {
    const h = pageHead("about", "de");
    return { meta: h.meta, links: h.links };
  },
  component: UeberUnsPage,
});

function UeberUnsPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Wer wir sind"
          headline={<>Wir sind Operator. <em>Nicht Verkäufer.</em></>}
          lead="Calvarius ist eine Digitalagentur. Seit 2013 betreuen wir mittelständische Unternehmen und ausgewählte Konzerne — national und international — in allem, was Online-Performance ausmacht. Wir arbeiten dabei wie ein internes Operations-Team, nicht wie ein externer Dienstleister."
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Über uns" }]}
        />

        <Section
          number="02"
          kicker="Founder"
          headline={<>André Freier — <em>operativ, nicht repräsentativ.</em></>}
          caption="Calvarius wird vom Geschäftsführer geführt. Das ist keine Floskel: André sitzt in jedem Kundenprojekt selbst am Konto, leitet die Audit-Reviews und verantwortet Strategie wie Tagesgeschäft."
        >
          <RevealOnScroll>
            <div className="mx-auto max-w-[680px] space-y-7 text-lg leading-[1.65] text-ink-2">
              <p>
                Ich habe Calvarius 2013 gegründet — mit einer einfachen
                Beobachtung: Online-Marketing funktioniert nur dann, wenn es
                wirtschaftlich gedacht wird. Was klein anfing, ist Methode
                geworden: Online-Performance als System zu denken statt als
                Stapel von Kanälen.
              </p>
              <p>
                Heute betreue ich operativ jedes Kundenprojekt selbst. Nicht weil das
                besonders skalierbar wäre — sondern weil es der einzige Weg
                ist, dass Strategie und Tagesgeschäft nicht auseinanderlaufen.
                Die meisten Performance-Probleme entstehen genau dort, wo die
                Person, die den Pitch geführt hat, das Konto nie wieder anfasst.
              </p>
              <p>
                Mein Hintergrund ist E-Commerce, deshalb stammen rund vier von
                fünf unserer Kundenprojekte aus dem Online-Handel. Mediabudgets
                zwischen 10 000 und 200 000 Euro im Monat, Sortimente von
                wenigen hundert bis mehreren tausend SKUs — national wie
                international. Was uns dabei zusammenhält: das gleiche Datenmodell
                für SEO, SEA, CRO und Tracking — und das gleiche Versprechen,
                dass der Geschäftsführer operativ am Konto sitzt.
              </p>
              <p>
                Calvarius arbeitet aus Karlstadt am Main, mitten in Mainfranken. Die Lage hat zwei Vorteile, die für unseren Mandate-Mix wirtschaftlich relevant sind: erstens sind Würzburg, Schweinfurt und Aschaffenburg in 25 bis 50 Minuten erreichbar — was persönliche Treffen mit regionalen Mandanten ohne logistischen Aufwand ermöglicht. Zweitens sind Frankfurt und Nürnberg in unter zwei Stunden erreichbar — was uns das gesamte Rhein-Main-Gebiet plus Mittelfranken als Einzugsgebiet eröffnet. Für bundesweite Mandate, die einen Großteil unseres Mix ausmachen, ist die operative Steuerung ohnehin digital — der Standort ist hier nur Anker, nicht Beschränkung. Für regionale Mandate führen wir{" "}
                <Link to="/regionen/wuerzburg/seo-agentur" className="text-cyan-deep underline-offset-4 hover:underline">SEO in unserer Region Würzburg</Link>{" "}
                als eigenständige Disziplin neben den überregionalen Performance-Mandaten.
              </p>
            </div>
          </RevealOnScroll>

          <Callout>
            Wenn die Person, die das Kundenprojekt verkauft hat, das Konto nie wieder
            sieht, läuft etwas grundsätzlich falsch.
          </Callout>
        </Section>

        <LogoReferences locale="de" number="03" />

        <Section
          number="04"
          variant="tinted"
          kicker="Wie wir arbeiten"
          headline={<>Drei Prinzipien, die <em>jedes Kundenprojekt tragen.</em></>}
          caption="Wir formulieren sie offen, weil wir wollen, dass Kunden sie als Vertrag mit uns verstehen — nicht als Marketing-Versprechen."
        >
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {[
                {
                  n: "01",
                  t: "Daten zuerst",
                  b: "Kein Kundenprojekt startet ohne Audit. Wenn die Datenlage nicht steht, ist jede Strategie geraten. Wir investieren lieber drei Wochen in solides Tracking als drei Monate in halb informierte Optimierung.",
                },
                {
                  n: "02",
                  t: "Operativ durch den GF",
                  b: "Kein Hand-off an Juniors nach dem Pitch. Strategie und Tagesgeschäft laufen über dieselbe Person, sonst entkoppelt sich beides innerhalb weniger Wochen.",
                },
                {
                  n: "03",
                  t: "Vergütung ohne Anreizkonflikt",
                  b: "Wir verdienen nicht mehr, wenn Sie mehr Mediabudget ausgeben. Pauschale oder Performance-bezogen — aber nie als Prozentsatz auf Ad-Spend.",
                },
              ].map((p) => (
                <div key={p.n} className="bg-white p-7">
                  <div className="font-mono text-xs text-ink-4">{p.n}</div>
                  <div className="mt-3 font-display text-xl text-ink">{p.t}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.b}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="05"
          kicker="Wann wir Nein sagen"
          headline={<>Manche Kundenprojekte sind <em>bei uns falsch.</em></>}
          caption="Ehrlichkeit vor Höflichkeit — auch im Erstgespräch. Hier vier Konstellationen, bei denen wir aktiv abwinken, statt einen Vertrag zu unterschreiben."
        >
          <RevealOnScroll>
            <div className="rounded-lg border-l-4 border-orange-500 bg-bg-subtle p-8 md:p-10">
              <ul className="space-y-5 text-base leading-relaxed text-ink-2">
                {[
                  "Wenn die Erwartung lautet, dass eine Agentur das Geschäftsmodell ersetzt — nicht beschleunigt.",
                  "Wenn das Tracking-Setup so kaputt ist, dass keine Optimierung valide gemessen werden kann, der Kunde es aber nicht reparieren möchte.",
                  "Wenn das Mediabudget so klein ist, dass eine seriöse Steuerung mehr kostet, als sie einbringen kann.",
                  "Wenn jemand eine prozentuale Vergütung auf den Mediaspend möchte. Das machen wir nicht — der Anreizkonflikt ist zu groß.",
                ].map((line) => (
                  <li key={line} className="flex gap-4">
                    <span className="mt-3 inline-block h-px w-5 shrink-0 bg-orange-500" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="06"
          variant="tinted"
          kicker="Standort"
          headline={<>Wir arbeiten <em>national und international.</em></>}
          caption="Wir arbeiten remote, wo es sinnvoll ist — und vor Ort, wo die Sache es trägt."
        >
          <RevealOnScroll>
            <div className="grid gap-10 rounded-lg border border-rule bg-white p-8 md:grid-cols-[1.4fr_1fr] md:p-12">
              <div>
                <h3 className="font-display text-2xl text-ink">
                  Ruhig arbeiten — verbindlich liefern.
                </h3>
                <p className="mt-4 leading-relaxed text-ink-3">
                  Unsere Arbeitsweise ist konzentriert, senior geführt und nah an den Konten. Was Kunden
                  spüren: tieferes Arbeiten, weniger Personal-Fluktuation,
                  mehr Senior-Stunden pro Euro.
                </p>
                <p className="mt-4 leading-relaxed text-ink-3">
                  Nationale wie internationale Kunden erreichen uns ohne
                  Reibungsverlust — wir kommen zu ihnen, wenn es wichtig ist.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-wider text-ink-4">
                  <span>seit 2013</span>
                  <span>·</span>
                  <span>national & international</span>
                </div>
              </div>
              <div className="flex items-center justify-center rounded-md bg-cyan-tint p-10">
                <div className="text-center">
                  <div className="mx-auto h-3 w-3 rounded-full bg-cyan-deep ring-4 ring-cyan/30 live-pulse" />
                  <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">
National & international
                  </div>
                </div>
              </div>
            </div>
            <EinzugsgebietMap locale="de" />
          </RevealOnScroll>
        </Section>

        <Section
          number="07"
          kicker="Sprechen wir"
          headline={<>Lassen Sie uns über Ihre <em>Performance</em> sprechen.</>}
          caption="Wenn Sie wissen möchten, wo Ihr Online-Marketing aktuell Potenzial verschenkt – und wie sich das strukturiert ändern lässt, sprechen wir gerne miteinander."
        >
          <CTABlock
            headline={<>Auf Augenhöhe — und ohne Verkaufsdruck.</>}
            body="Schreiben Sie uns kurz, worum es geht. Wir melden uns innerhalb von zwei Werktagen mit einem Vorschlag für ein Erstgespräch oder einer ehrlichen Einschätzung, falls wir nicht die richtige Adresse sind."
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Leistungen ansehen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
